import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Pencil } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/Card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/Dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/Tabs";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/Carousel";
import { Skeleton } from "@/components/ui/Skeleton";
import { Button } from "@/components/ui/Button";
import { CarouselIndicator } from "@/components/CarouselIndicator";
import { toast } from "sonner";
import { useMyProfile } from "@/hooks/useMyProfile";
import { useProfileQuestions } from "@/hooks/useProfileQuestions";
import { useProfileAnswers } from "@/hooks/useProfileAnswers";
import { useCreateProfileAnswer } from "@/hooks/useCreateProfileAnswer";
import { useUpdateProfileAnswer } from "@/hooks/useUpdateProfileAnswer";
import { useDestroyProfileAnswer } from "@/hooks/useDestroyProfileAnswer";
import type { ProfileQuestion, ProfileAnswer } from "@/__generated__/types";

const ANSWER_MAX_LENGTH = 65;

interface DialogState {
  open: boolean;
  question: ProfileQuestion | null;
  existingAnswer: ProfileAnswer | null;
}

interface FunFactCarouselProps<T> {
  items: T[];
  loading: boolean;
  emptyStateMessage: string;
  renderCard: (item: T) => React.ReactNode;
}

const FunFactCarousel = <T,>({
  items,
  loading,
  emptyStateMessage,
  renderCard,
}: FunFactCarouselProps<T>) => {
  const { t } = useTranslation();
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [totalSlides, setTotalSlides] = useState(0);

  useEffect(() => {
    if (!carouselApi) return;

    const updateCarouselState = (): void => {
      setCurrentSlide(carouselApi.selectedScrollSnap());
      setTotalSlides(carouselApi.scrollSnapList().length);
    };

    updateCarouselState();
    carouselApi.on("select", updateCarouselState);
    carouselApi.on("reInit", updateCarouselState);

    return () => {
      carouselApi.off("select", updateCarouselState);
      carouselApi.off("reInit", updateCarouselState);
    };
  }, [carouselApi]);

  useEffect(() => {
    if (!carouselApi) return;
    carouselApi.reInit();
  }, [items, carouselApi]);

  const scrollToSlide = (index: number): void => {
    carouselApi?.scrollTo(index);
  };

  if (loading) {
    return (
      <Carousel
        opts={{
          align: "start",
          slidesToScroll: 1,
          containScroll: "trimSnaps",
        }}
        className="relative w-full px-[1px]"
      >
        <CarouselContent className="-ml-4">
          {[1, 2, 3].map((i) => (
            <CarouselItem key={i} className="basis-auto pl-4">
              <Skeleton className="h-[168px] w-[228px] rounded-xl" />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        {emptyStateMessage}
      </div>
    );
  }

  return (
    <div className="relative flex w-full flex-col gap-2 rounded-xl">
      <Carousel
        setApi={setCarouselApi}
        opts={{
          align: "start",
          slidesToScroll: 1,
          containScroll: "trimSnaps",
        }}
        className="relative w-full"
      >
        <CarouselContent className="-ml-4">
          {items.map((item, index) => (
            <CarouselItem key={index} className="basis-auto pl-4">
              {renderCard(item)}
            </CarouselItem>
          ))}
        </CarouselContent>

        <CarouselPrevious className="absolute top-1/2 -left-[16px] -translate-y-1/2 rounded-full border p-2.5 shadow-md transition-opacity duration-300 disabled:pointer-events-none disabled:opacity-0" />
        <CarouselNext className="absolute top-1/2 -right-[16px] -translate-y-1/2 rounded-full border p-2.5 shadow-md transition-opacity duration-300 disabled:pointer-events-none disabled:opacity-0" />
      </Carousel>

      {/* Carousel indicators - only show if we have multiple slides */}
      {totalSlides > 1 && (
        <CarouselIndicator
          totalSlides={totalSlides}
          currentSlide={currentSlide}
          onDotClick={scrollToSlide}
          getAriaLabel={(slideNum) =>
            t("studentCard.funFacts.dotAria", { slideNum })
          }
        />
      )}
    </div>
  );
};

export const EditFunFacts = () => {
  const { t } = useTranslation();
  const { profile, loading: profileLoading } = useMyProfile();

  const { questions, loading: questionsLoading } = useProfileQuestions({
    notAnsweredByUserId: profile?.id,
    skip: !profile?.id,
  });

  const { answers, loading: answersLoading } = useProfileAnswers({
    userId: profile?.id ?? "",
    skip: !profile?.id,
  });

  const [dialogState, setDialogState] = useState<DialogState>({
    open: false,
    question: null,
    existingAnswer: null,
  });

  const [answerText, setAnswerText] = useState("");
  const [activeTab, setActiveTab] = useState("incomplete");

  const closeDialog = () => {
    setDialogState({
      open: false,
      question: null,
      existingAnswer: null,
    });
    setAnswerText("");
  };

  const [createAnswer, { loading: createLoading }] = useCreateProfileAnswer({
    onCompleted: () => {
      toast.success(t("manageProfile.funFacts.toast.createSuccess"));
      closeDialog();
    },
    onError: () => {
      toast.error(t("manageProfile.funFacts.toast.error"));
    },
  });

  const [updateAnswer, { loading: updateLoading }] = useUpdateProfileAnswer({
    onCompleted: () => {
      toast.success(t("manageProfile.funFacts.toast.updateSuccess"));
      closeDialog();
    },
    onError: () => {
      toast.error(t("manageProfile.funFacts.toast.error"));
    },
  });

  const { destroyProfileAnswer, loading: destroyLoading } =
    useDestroyProfileAnswer({
      onSuccess: closeDialog,
      onError: closeDialog,
    });

  const isLoading = profileLoading || questionsLoading || answersLoading;
  const isSaving = createLoading || updateLoading || destroyLoading;

  const openDialogForAnswer = (question: ProfileQuestion) => {
    setDialogState({
      open: true,
      question,
      existingAnswer: null,
    });
    setAnswerText("");
  };

  const openDialogForEdit = (answer: ProfileAnswer) => {
    setDialogState({
      open: true,
      question: answer.question,
      existingAnswer: answer,
    });
    setAnswerText(answer.text ?? "");
  };

  const handleSave = async () => {
    if (!profile?.id || !dialogState.question) return;

    const trimmedAnswer = answerText.trim();

    if (dialogState.existingAnswer && trimmedAnswer === "") {
      await destroyProfileAnswer(dialogState.existingAnswer.id, profile.id);
      return;
    }

    if (!dialogState.existingAnswer && trimmedAnswer === "") {
      closeDialog();
      return;
    }

    if (dialogState.existingAnswer) {
      await updateAnswer({
        userId: profile.id,
        profileAnswerId: dialogState.existingAnswer.id,
        answer: trimmedAnswer,
      });
    } else {
      await createAnswer({
        userId: profile.id,
        profileQuestionId: dialogState.question.id,
        answer: trimmedAnswer,
      });
    }
  };

  const isOverLimit = answerText.length >= ANSWER_MAX_LENGTH;
  const isSaveDisabled =
    isSaving || (!dialogState.existingAnswer && answerText.trim() === "");

  const characterCount = useMemo(
    () => `${answerText.length}/${ANSWER_MAX_LENGTH}`,
    [answerText.length],
  );

  return (
    <>
      <Card className="w-[766px] rounded-lg bg-card shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-border">
        <CardHeader className="px-6 pt-6">
          <CardTitle className="text-xl font-semibold text-card-foreground">
            {t("manageProfile.funFacts.title")}
          </CardTitle>
          <CardDescription className="text-sm text-muted-foreground">
            {t("manageProfile.funFacts.subTitle")}
          </CardDescription>
        </CardHeader>

        <CardContent className="px-6 pb-6">
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="h-10 rounded-md bg-muted p-1">
              <TabsTrigger
                value="incomplete"
                className="rounded-sm px-3 py-1.5"
              >
                {t("manageProfile.funFacts.tabs.incomplete")}
              </TabsTrigger>
              <TabsTrigger value="completed" className="rounded-sm px-3 py-1.5">
                {t("manageProfile.funFacts.tabs.completed")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="incomplete" className="pt-6">
              <FunFactCarousel
                items={questions}
                loading={isLoading}
                emptyStateMessage={t(
                  "manageProfile.funFacts.emptyStates.noUnanswered",
                )}
                renderCard={(question) => {
                  if (!question) return null;
                  return (
                    <div className="flex h-[168px] w-[228px] flex-col justify-between rounded-xl border border-border bg-background p-6">
                      <div className="text-sm font-bold text-foreground">
                        {question.text}
                      </div>
                      <Button
                        onClick={() => openDialogForAnswer(question)}
                        className="h-10 w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
                      >
                        {t("manageProfile.funFacts.buttons.answer")}
                      </Button>
                    </div>
                  );
                }}
              />
            </TabsContent>

            <TabsContent value="completed" className="pt-6">
              <FunFactCarousel
                items={answers}
                loading={isLoading}
                emptyStateMessage={t(
                  "manageProfile.funFacts.emptyStates.noAnswered",
                )}
                renderCard={(answer) => {
                  if (!answer) return null;
                  return (
                    <div className="relative flex h-[162px] w-[228px] flex-col rounded-xl border border-border bg-background p-6">
                      <div className="mb-4 line-clamp-2 text-sm font-bold text-foreground">
                        {answer.question.text}
                      </div>
                      <div className="flex flex-1 items-start">
                        <div className="line-clamp-3 text-xl leading-tight font-semibold text-foreground">
                          {answer.text}
                        </div>
                      </div>
                      <button
                        onClick={() => openDialogForEdit(answer)}
                        className="absolute right-6 bottom-6 h-4 w-4 text-muted-foreground transition-colors hover:text-foreground"
                        aria-label={t("manageProfile.funFacts.buttons.edit")}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    </div>
                  );
                }}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Dialog open={dialogState.open} onOpenChange={closeDialog}>
        <DialogContent
          className="max-w-md rounded-lg bg-background p-6 shadow-lg outline outline-1 outline-offset-[-1px] outline-border"
          aria-describedby={undefined}
        >
          <DialogHeader className="flex flex-col gap-1.5">
            <DialogTitle className="text-lg font-semibold text-foreground">
              {dialogState.question?.text}
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4 flex flex-col gap-2">
            <input
              type="text"
              value={answerText}
              onChange={(e) => {
                if (e.target.value.length <= ANSWER_MAX_LENGTH) {
                  setAnswerText(e.target.value);
                }
              }}
              placeholder=""
              className="h-10 rounded-md bg-background px-3 py-2 text-sm text-foreground outline outline-1 outline-offset-[-1px] outline-input focus:outline-ring"
              disabled={isSaving}
              maxLength={ANSWER_MAX_LENGTH}
            />
            <div
              className={`text-sm ${
                isOverLimit ? "text-destructive" : "text-muted-foreground"
              }`}
            >
              {characterCount}
            </div>
          </div>

          <div className="mt-4 flex items-center justify-end gap-4">
            <Button
              onClick={closeDialog}
              disabled={isSaving}
              variant="secondary"
              className="h-10 rounded-md bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground"
            >
              {t("global.cancel")}
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaveDisabled}
              className="h-10 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  {t("global.save")}
                </span>
              ) : (
                t("global.save")
              )}
            </Button>
          </div>

          <DialogClose className="absolute top-4 right-4 h-4 w-4 opacity-70 transition-opacity hover:opacity-100" />
        </DialogContent>
      </Dialog>
    </>
  );
};
